import { Link, useCanGoBack, useRouter } from '@tanstack/react-router'

import { ChevronLeft } from 'lucide-react'

import { useIsDesktop } from '@/hooks/use-desktop'
import { cn } from '@/utils/ui/styles'

import { Button } from '../ui/button'

import type { ButtonProps } from '../ui/button'
import type { LinkOptions } from '@tanstack/react-router'

type BackLinkProps = {
	to: LinkOptions['to']
	/**
	 * Route params for `to`, when the fallback destination is a param route.
	 *
	 * Loosely typed on purpose: `to` here is the whole route union, so there is
	 * no single params shape to bind it to. The value is only ever read on the
	 * cold-load path (see below), where a wrong key surfaces immediately as a
	 * missing segment rather than silently.
	 */
	params?: LinkOptions['params']
	className?: string
}

/**
 * A button that navigates back to the previous page.
 * @param {LinkOptions['to']} to - The URL to navigate to.
 * @param {string} [className] - The class name to apply to the link.
 *
 * @see https://tanstack.com/router/latest/docs/framework/react/api/router/useCanGoBack#showing-a-back-button
 *
 * @example
 * <BackLink to="/air/case-management">
 * 	<ChevronLeft className="h-4 w-4" />
 * 	<span className="sm:hidden">Back</span>
 * </BackLink>
 * @returns
 */
const BackLink = ({ to, params, className }: BackLinkProps) => {
	const router = useRouter()
	const canGoBack = useCanGoBack()
	const isDesktop = useIsDesktop()

	const buttonProps: ButtonProps = {
		'variant': 'outline',
		'size': isDesktop ? 'icon' : 'default',
		'className': cn(
			'aspect-square w-full justify-start gap-2 sm:size-10 sm:justify-center',
			className,
		),
		'aria-label': 'Go Back',
	}

	// If we can't safely go back (e.g. leaves the app, history was rewritten, etc), use a Link with `to` prop
	if (!canGoBack) {
		return (
			<Button
				{...buttonProps}
				nativeButton={false}
				render={
					<Link to={to} params={params}>
						<ButtonContent />
					</Link>
				}
			/>
		)
	}

	// Otherwise, use a Button with `onClick` prop
	return (
		<Button {...buttonProps} onClick={() => router.history.back()}>
			<ButtonContent />
		</Button>
	)
}

function ButtonContent() {
	return (
		<>
			<ChevronLeft className="h-4 w-4" />
			<span className="sm:hidden">Back</span>
		</>
	)
}

export default BackLink
